import * as React from 'react';
import {
    List,
    Datagrid,
    TextField,
    NumberField,
    TopToolbar,
    CreateButton,
    useRecordContext,
    useDataProvider,
    DataProviderContext,
} from 'react-admin';
import FormattedDateTimeField from '../../common/FormattedDateTimeField';
import { DeleteWithDialogAction } from '../../common/DeleteWithDialogAction';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { LoadingSection } from '../../common/LoadingSection';
import { NoDataSection } from '../../common/NoDataSection';
import Button from '@mui/material/Button';
import StatusTags from '../../common/StatusTags';
import DimensionsField from '../../common/DimensionsFeild';
import { Ecom_Products } from '../../custom';
import { Visibility } from '@mui/icons-material';
import { createCustomGoDataProvider } from '../../services';
import ConfirmDeleteButton from '../../common/ConfirmDeleteButton';

const ListActions = ({
    categoryId,
    productTemplateId,
}: {
    productTemplateId: string;
    categoryId: string;
}) => {
    const navigate = useNavigate();
    return (
        <TopToolbar>
            <CreateButton
                onClick={e => {
                    e.preventDefault();
                    navigate(
                        `/ecom_categories/${categoryId}/ecom_product_templates/${productTemplateId}/ecom_products/create`,
                    );
                }}
            />
        </TopToolbar>
    );
};

const EditButton = ({
    categoryId,
    productTemplateId,
}: {
    productTemplateId: string;
    categoryId: string;
}) => {
    const record = useRecordContext();
    const navigate = useNavigate();
    return (
        <Button
            onClick={() => {
                navigate(
                    `/ecom_categories/${categoryId}/ecom_product_templates/${productTemplateId}/ecom_products/${record.id}/edit`,
                );
            }}
        >
            Edit
        </Button>
    );
};

const ShowButton = ({
    categoryId,
    productTemplateId,
}: {
    productTemplateId: string;
    categoryId: string;
}) => {
    const navigate = useNavigate();
    const record: Ecom_Products = useRecordContext<Ecom_Products>();
    return (
        <Button
            onClick={e => {
                navigate(
                    `/ecom_categories/${categoryId}/ecom_product_templates/${productTemplateId}/ecom_products/${record.id}/show`,
                );
            }}
        >
            <Visibility />
        </Button>
    );
};

const ProductList = () => {
    const { categoryId, productTemplateId } = useParams();
    const navigate = useNavigate();
    if (!productTemplateId) {
        return <LoadingSection />;
    }
    const baseDataProvider = useDataProvider();
    const customDataProvider = React.useMemo(
        () =>
            createCustomGoDataProvider(baseDataProvider, [
                { resource: 'ecom_products', apiEndpoint: '/ecom/products' },
            ]),
        [baseDataProvider],
    );
    return (
        <DataProviderContext.Provider value={customDataProvider}>
            <List
                empty={
                    <NoDataSection
                        resource={'ecom_products'}
                        onCreate={() => {
                            navigate(
                                `/ecom_categories/${categoryId}/ecom_product_templates/${productTemplateId}/ecom_products/create`,
                            );
                        }}
                    />
                }
                actions={<ListActions productTemplateId={productTemplateId || ''} categoryId={categoryId || ''} />}
                filter={{ productTemplateId: productTemplateId }}
                resource={'ecom_products'}
            >
                <Datagrid>
                    <TextField source="name" />
                    <StatusTags source="status" label={'Status'} />
                    <NumberField source="availableQuantity" />
                    <NumberField source="appleAmount" />
                    <NumberField source="amount" />
                    <NumberField source="discount" />
                    <NumberField source="deliveryAmount" />
                    <DimensionsField />
                    <FormattedDateTimeField source="createdAt" />
                    <EditButton productTemplateId={productTemplateId} categoryId={categoryId || ''} />
                    <ConfirmDeleteButton path={`ecom_categories/${categoryId}/ecom_product_templates/${productTemplateId}/ecom_products`} />
                    <ShowButton productTemplateId={productTemplateId} categoryId={categoryId || ''} />
                </Datagrid>
            </List>
        </DataProviderContext.Provider>
    );
};
export default ProductList;
